"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { Icon } from "@iconify/react";

const ICON =
  "h-4 w-4 sm:h-[18px] sm:w-[18px] md:h-[20px] md:w-[20px] text-white shrink-0";

function IconInstagram() {
  return (
    <svg className={ICON} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function IconFacebook() {
  return (
    <svg
      className={ICON}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function IconWhatsApp() {
  return (
    <svg className={ICON} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

function IconX() {
  return (
    <svg className={ICON} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/** Button size per breakpoint */
const BTN_SIZE = "h-8 w-8 sm:h-10 sm:w-10 md:h-11 md:w-11";
/** Bar height for horizontal mode (sm+) */
const BAR_H_SM =
  "sm:h-10 sm:min-h-[2.5rem] sm:max-h-[2.5rem] md:h-11 md:min-h-[2.75rem] md:max-h-[2.75rem]";

const iconLink =
  "flex items-center justify-center rounded-full p-1.5 text-white transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50";

export interface ProjectShareHoverMenuProps {
  url: string;
  text: string;
  className?: string;
  /** Overrides teal share segment (e.g. bg-association-theme-2) */
  shareSegmentClassName?: string;
  /** When true, use vertical layout up to lg breakpoint (for narrow cards with 3+ items per row) */
  compact?: boolean;
  labels?: {
    twitter?: string;
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
  };
}

/**
 * Collapsed: 44px squircle with share icon only.
 * Expanded: same height — black icon tray + teal segment with label + icon.
 */
export function ProjectShareHoverMenu({
  url,
  text,
  className,
  shareSegmentClassName,
  compact = false,
  labels = {},
}: ProjectShareHoverMenuProps) {
  const t = useTranslations("common");
  const shareLabel = t("share");
  const [resolvedUrl, setResolvedUrl] = useState(url);
  const encodedUrl = encodeURIComponent(resolvedUrl);
  const encodedText = encodeURIComponent(text);
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${text} ${resolvedUrl}`.trim())}`;

  const rootRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [touchOpen, setTouchOpen] = useState(false);
  const [coarsePointer, setCoarsePointer] = useState(false);

  useEffect(() => {
    setResolvedUrl(
      url.startsWith("http") ? url : `${window.location.origin}${url}`,
    );
  }, [url]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(pointer: coarse)");
    const apply = () => setCoarsePointer(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!coarsePointer || !touchOpen) return;
    const close = (e: Event) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setTouchOpen(false);
    };
    document.addEventListener("click", close, true);
    return () => document.removeEventListener("click", close, true);
  }, [coarsePointer, touchOpen]);

  const expanded = hovered || touchOpen;

  const stopCardClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  const socialLinks = (
    <>
      <a
        href={`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={iconLink}
        aria-label={labels.twitter ?? "X"}
        onClick={stopCardClick}
      >
        <IconX />
      </a>
      <a
        href={`https://www.facebook.com/sharer.php?t=${encodedText}&u=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={iconLink}
        aria-label={labels.facebook ?? "Facebook"}
        onClick={stopCardClick}
      >
        <IconFacebook />
      </a>
      <a
        href={`https://www.instagram.com/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={iconLink}
        aria-label={labels.instagram ?? "Instagram"}
        onClick={stopCardClick}
      >
        <IconInstagram />
      </a>
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        className={iconLink}
        aria-label={labels.whatsapp ?? "WhatsApp"}
        onClick={stopCardClick}
      >
        <IconWhatsApp />
      </a>
    </>
  );

  return (
    <div
      ref={rootRef}
      className={cn("z-20", className)}
      onMouseEnter={() => {
        if (!coarsePointer) setHovered(true);
      }}
      onMouseLeave={() => {
        if (!coarsePointer) setHovered(false);
      }}
    >
      {/* ─── Horizontal expand: sm+ normally, lg+ when compact ─── */}
      <div
        className={cn(
          "w-max rounded-2xl items-stretch overflow-hidden shadow-md transition-[border-radius] duration-300 ease-out",
          compact ? "hidden lg:inline-flex" : "hidden sm:inline-flex",
          BAR_H_SM,
        )}
      >
        <button
          type="button"
          aria-label={shareLabel}
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            if (coarsePointer) setTouchOpen((o) => !o);
          }}
          className={cn(
            "flex shrink-0 items-center justify-center font-semibold text-white transition-all duration-300 ease-out",
            BAR_H_SM,
            "hover:brightness-110",
            expanded
              ? "gap-2 px-3 text-sm md:px-4 md:text-base"
              : "w-10 min-w-[2.5rem] md:w-11 md:min-w-[2.75rem] p-0",
            shareSegmentClassName,
          )}
        >
          {expanded ? (
            <>
              <Icon
                icon="material-symbols:share-outline"
                className="w-3 h-3 md:w-6 md:h-6"
              />
              <span className="whitespace-nowrap leading-none">
                {shareLabel}
              </span>
            </>
          ) : (
            <Icon
              icon="material-symbols:share-outline"
              className="w-3 h-3 md:w-6 md:h-6"
            />
          )}
        </button>

        <div
          className={cn(
            "flex max-w-0 flex-nowrap items-center justify-center gap-1 md:gap-2 overflow-hidden bg-black transition-[max-width,padding] duration-300 ease-out",
            BAR_H_SM,
            expanded && "max-w-[220px] px-2 md:max-w-[260px] md:px-3",
          )}
          aria-hidden={!expanded}
        >
          {socialLinks}
        </div>
      </div>

      {/* ─── Vertical dropdown: below sm normally, below lg when compact ─── */}
      <div
        className={cn(
          "inline-flex flex-col items-center overflow-hidden rounded-xl shadow-md transition-all duration-200",
          compact ? "lg:hidden" : "sm:hidden",
          expanded ? "bg-black/90" : "",
        )}
      >
        <button
          type="button"
          aria-label={shareLabel}
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setTouchOpen((o) => !o);
          }}
          className={cn(
            "flex shrink-0 items-center justify-center text-white transition-all duration-200",
            BTN_SIZE,
            expanded ? "rounded-t-xl rounded-b-none" : "rounded-xl",
            shareSegmentClassName,
          )}
        >
          <Icon
            icon="material-symbols:share-outline"
            className="w-3 h-3 md:w-6 md:h-6"
          />
        </button>

        <div
          className={cn(
            "flex flex-col items-center overflow-hidden transition-all duration-200 ease-out",
            expanded
              ? "max-h-[200px] gap-0.5 py-1 opacity-100"
              : "max-h-0 gap-0 py-0 opacity-0 pointer-events-none",
          )}
          aria-hidden={!expanded}
        >
          {socialLinks}
        </div>
      </div>
    </div>
  );
}
